import React from 'react'
import styled from '@emotion/styled'
import { colors, radius, spacing } from '@mobi/component-library/Theme/Common'
import { TicketDisplayContext } from '@core/Areas/AccountActivity/context'
import { BetInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/BetInfo'
import { SelectionInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/Details/Components/SelectionInfo'
import {
  getTicketLayoutType,
  type TicketLayoutType,
} from '@core/Areas/AccountActivity/Components/Ticket/helpers/legTypes'
import type { FollowMeSharedBet } from '@core/Areas/FollowMe/types'
import { trackFollowMeExpandSelection } from '@core/Areas/FollowMe/helpers/analytics'
import { HeightTruncateWrapper } from './Components/HeightTruncateWrapper'

const enum LocalConstants {
  ShareBetCardContainerClass = 'bet-selection-display__card',
}

export const BetSelectionDisplay: React.FC<{ sharedBet: FollowMeSharedBet }> = ({ sharedBet }) => {
  const ticket = sharedBet.ticket
  const ticketTypeLayout = getTicketLayoutType(ticket)

  const isHeightTruncateEnabled = shouldEnableHeightTruncate(ticket, ticketTypeLayout)

  const handleOnChangeCallback = (expanded: boolean) => {
    trackFollowMeExpandSelection(expanded ? 'enable' : 'disable')
  }

  return (
    <WrapperStyled>
      <HeightTruncateWrapper
        isEnabled={isHeightTruncateEnabled}
        onChangeCallback={handleOnChangeCallback}
      >
        <TicketDisplayContext.Provider
          value={{ type: 'share-preview', data: { shouldHideCost: true, isInFeed: true } }}
        >
          <div className={LocalConstants.ShareBetCardContainerClass}>
            <BetInfo ticket={ticket} status='Pending' ticketLayoutType={ticketTypeLayout} />

            <SelectionInfo ticket={ticket} />
          </div>
        </TicketDisplayContext.Provider>
      </HeightTruncateWrapper>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function shouldEnableHeightTruncate(
  ticket: FollowMeSharedBet['ticket'],
  layoutType: TicketLayoutType
): boolean {
  if (layoutType === 'multi' && ticket.legs.length > 2) return true
  if (layoutType === 'single-multiple') {
    const acceptorCount = ticket.legs[0].selections.reduce(
      (count, selection) => selection.selectionDetails[0].acceptorDetails.length + count,
      0
    )
    return acceptorCount > 6
  }

  return false
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  padding: spacing.sm,
  borderRadius: radius.lgx1,
  backgroundImage: `repeating-linear-gradient(26deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px), repeating-linear-gradient(116deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px), repeating-linear-gradient(206deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px), repeating-linear-gradient(296deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px)`,
  backgroundSize: '1px 100%, 100% 1px, 1px 100% , 100% 1px',
  backgroundPosition: '0 0, 0 0, 100% 0, 0 100%',
  backgroundRepeat: 'no-repeat',

  [`.${LocalConstants.ShareBetCardContainerClass}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
  },
})
